import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Gavel, ChevronLeft, Package, Store } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { AuctionBadge, aucPrice, aucBids, aucEnd, aucStatus, timeLeft } from "../components/AuctionSection";

export default function AuctionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const { fmt } = useCurrency();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [amount, setAmount] = useState("");
  const [bidding, setBidding] = useState(false);
  const [, setTick] = useState(0);

  const load = () =>
    api(`/auctions/${id}`)
      .then((d) => {
        setA(d);
        const next = (d.current_price ?? d.current_bid ?? d.start_price ?? 0) + (d.min_increment || 1);
        setAmount(String(next));
      })
      .catch(() => setNotFound(true));

  useEffect(() => {
    setA(null);
    setNotFound(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // live countdown tick
  useEffect(() => {
    const iv = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  const placeBid = async () => {
    if (!user) { navigate(`/login?next=/auctions/${id}`); return; }
    const val = Number(amount);
    if (!val || val <= 0) { toast.error(t("bid_invalid")); return; }
    setBidding(true);
    try {
      await api(`/auctions/${id}/bids`, { method: "POST", auth: true, body: { amount: val } });
      toast.success(t("bid_success"));
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBidding(false);
    }
  };

  if (notFound)
    return (
      <div data-testid="auction-not-found" className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="font-heading text-lg font-bold text-slate-800">{t("auc_not_found")}</p>
        <Link to="/auctions" className="mt-3 inline-block text-sm font-semibold text-teal-700 hover:underline">{t("view_auctions")}</Link>
      </div>
    );
  if (!a) return <div data-testid="auction-loading" className="py-16 text-center text-sm text-slate-500">...</div>;

  const status = aucStatus(a);
  const end = aucEnd(a);
  const minNext = (a.current_price ?? a.current_bid ?? a.start_price ?? 0) + (a.min_increment || 1);
  const bids = Array.isArray(a.bids) ? a.bids : [];

  return (
    <div data-testid="auction-detail" className="mx-auto max-w-4xl px-4 pb-16 sm:px-6">
      <header className="sticky top-14 z-30 -mx-4 flex items-center gap-2 border-b border-slate-100 bg-white/95 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6">
        <button data-testid="auction-back" onClick={() => navigate("/auctions")} aria-label="Back" className="rounded-full p-1.5 text-slate-700 transition hover:bg-teal-50 active:scale-90">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="truncate font-heading text-lg font-bold tracking-tight text-slate-900">{t("nav_auctions")}</h1>
      </header>

      <div className="grid gap-6 pt-5 md:grid-cols-2">
        <div className="relative aspect-square overflow-hidden rounded-3xl bg-teal-50">
          {a.image ? (
            <img data-testid="auction-image" src={mediaUrl(a.image)} alt={a.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-teal-300"><Package className="h-16 w-16" /></div>
          )}
          <span className="absolute left-3 top-3"><AuctionBadge status={status} /></span>
        </div>

        <div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{a.title}</h2>
          {a.seller_name && (
            <Link to={a.store_key ? `/store/${a.store_key}` : "#"} className="mt-1 flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700">
              <Store className="h-4 w-4" /> {a.seller_name}
            </Link>
          )}

          <div className="mt-5 rounded-2xl border border-orange-200 bg-orange-50/60 p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("current_bid")}</p>
            <p data-testid="auction-current-price" className="mt-1 text-3xl font-extrabold text-orange-600">{fmt(aucPrice(a))}</p>
            <p className="mt-1 text-xs text-slate-500">
              {aucBids(a)} {t("auc_bids")}
              {a.min_increment ? ` · ${t("min_increment")} ${fmt(a.min_increment)}` : ""}
            </p>
            {end && status === "live" && (
              <p data-testid="auction-countdown" className="mt-3 rounded-xl bg-white px-3 py-2 text-center font-mono text-lg font-extrabold tabular-nums text-slate-900 ring-1 ring-orange-200">
                {timeLeft(end, t)}
              </p>
            )}
          </div>

          {status === "live" && (
            <div className="mt-4 flex gap-2">
              <input
                data-testid="bid-amount-input"
                type="number"
                min={minNext}
                step={a.min_increment || 1}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-lg font-bold outline-none focus:border-teal-500"
                placeholder={String(minNext)}
              />
              <button
                data-testid="place-bid-button"
                onClick={placeBid}
                disabled={bidding}
                className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-teal-700 px-6 py-3 text-sm font-bold text-white shadow-md shadow-teal-700/25 transition hover:bg-teal-800 active:scale-95 disabled:opacity-60"
              >
                <Gavel className="h-4 w-4" /> {bidding ? "..." : t("place_bid")}
              </button>
            </div>
          )}
          <p className="mt-2 text-[11px] text-slate-400">{t("bid_hint")} {fmt(minNext)}</p>

          {bids.length > 0 && (
            <div className="mt-6">
              <h3 className="font-heading text-sm font-bold text-slate-900">{t("bid_history")}</h3>
              <div className="mt-2 divide-y divide-slate-100 rounded-2xl bg-white ring-1 ring-slate-100">
                {bids.slice(0, 8).map((b, i) => (
                  <div key={i} data-testid={`bid-row-${i}`} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-slate-600">{b.bidder || b.user_name || b.user || "—"}</span>
                    <span className="font-bold text-slate-900">{fmt(b.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {a.product_id && (
            <Link data-testid="auction-product-link" to={`/product/${a.product_id}`} className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:underline">
              <Package className="h-4 w-4" /> {t("view_product")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
